"use client";

import { useEffect, useRef } from "react";

interface RichTextRendererProps {
  content: string;
  className?: string;
}

/**
 * RichTextRenderer component for displaying WordPress content
 * 
 * Sanitizes HTML from WordPress and applies PNN typography classes and color palette.
 * Handles images responsively and ensures proper styling.
 * 
 * Note: For production, consider adding DOMPurify for more robust HTML sanitization.
 */
export default function RichTextRenderer({
  content,
  className = "",
}: RichTextRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Apply PNN styling to WordPress content
    // Style headings
    const headings = container.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headings.forEach((heading) => {
      const headingEl = heading as HTMLElement;
      headingEl.classList.add("font-bold");
      headingEl.style.color = "#0A3740"; // PNN dark teal
      headingEl.style.fontFamily = "var(--font-montserrat)";
      
      if (heading.tagName === "H1") {
        headingEl.classList.add("text-4xl", "md:text-5xl", "mb-6", "mt-8");
      } else if (heading.tagName === "H2") {
        headingEl.classList.add("text-3xl", "md:text-4xl", "mb-4", "mt-6");
      } else if (heading.tagName === "H3") {
        headingEl.classList.add("text-2xl", "md:text-3xl", "mb-3", "mt-5");
      } else {
        headingEl.classList.add("text-xl", "md:text-2xl", "mb-2", "mt-4");
      }
    });

    // Style paragraphs
    const paragraphs = container.querySelectorAll("p");
    paragraphs.forEach((p) => {
      const pEl = p as HTMLElement;
      pEl.classList.add("mb-4", "leading-relaxed");
      pEl.style.color = "#22333B"; // PNN text dark
      pEl.style.fontSize = "1rem";
      pEl.style.lineHeight = "1.75";
    });

    // Style links
    const links = container.querySelectorAll("a");
    links.forEach((link) => {
      const linkEl = link as HTMLAnchorElement;
      linkEl.classList.add("underline", "hover:opacity-80", "transition-opacity");
      linkEl.style.color = "#0A3740"; // PNN dark teal
      // Open external links in new tab
      if (linkEl.href && !linkEl.href.startsWith(window.location.origin)) {
        linkEl.target = "_blank";
        linkEl.rel = "noopener noreferrer";
      }
    });

    // Style lists
    const lists = container.querySelectorAll("ul, ol");
    lists.forEach((list) => {
      const listEl = list as HTMLElement;
      listEl.classList.add("mb-4", "ml-6");
      listEl.style.color = "#22333B";
    });

    const listItems = container.querySelectorAll("li");
    listItems.forEach((li) => {
      const liEl = li as HTMLElement;
      liEl.classList.add("mb-2");
      liEl.style.color = "#22333B";
    });

    // Style blockquotes
    const blockquotes = container.querySelectorAll("blockquote");
    blockquotes.forEach((blockquote) => {
      const blockquoteEl = blockquote as HTMLElement;
      blockquoteEl.classList.add("border-l-4", "pl-4", "my-4", "italic");
      blockquoteEl.style.borderColor = "#D4AF37"; // PNN gold
      blockquoteEl.style.color = "rgba(10, 55, 64, 0.8)";
    });

    // Make images responsive
    const images = container.querySelectorAll("img");
    images.forEach((img) => {
      const imgEl = img as HTMLImageElement;
      imgEl.classList.add("max-w-full", "h-auto", "rounded-lg", "my-6");
      // Ensure images don't break layout
      imgEl.style.maxWidth = "100%";
      imgEl.style.height = "auto";
      // Add loading="lazy" for performance
      if (!imgEl.hasAttribute("loading")) {
        imgEl.setAttribute("loading", "lazy");
      }
      // Add alt text if missing
      if (!imgEl.alt) {
        imgEl.alt = "Content image";
      }
    });

    // Style code blocks
    const codeBlocks = container.querySelectorAll("pre, code");
    codeBlocks.forEach((code) => {
      const codeEl = code as HTMLElement;
      codeEl.classList.add("bg-gray-100", "p-2", "rounded", "text-sm", "font-mono");
      codeEl.style.color = "#22333B";
    });

    // Style tables
    const tables = container.querySelectorAll("table");
    tables.forEach((table) => {
      const tableEl = table as HTMLElement;
      tableEl.classList.add("w-full", "border-collapse", "my-6");
      const cells = tableEl.querySelectorAll("td, th");
      cells.forEach((cell) => {
        const cellEl = cell as HTMLElement;
        cellEl.classList.add("border", "px-4", "py-2");
        cellEl.style.borderColor = "rgba(10, 55, 64, 0.2)";
        cellEl.style.color = "#22333B";
      });
      const headers = tableEl.querySelectorAll("th");
      headers.forEach((header) => {
        const headerEl = header as HTMLElement;
        headerEl.classList.add("bg-gray-50", "font-semibold");
        headerEl.style.backgroundColor = "rgba(10, 55, 64, 0.05)";
      });
    });

    // Style horizontal rules
    const horizontalRules = container.querySelectorAll("hr");
    horizontalRules.forEach((hr) => {
      const hrEl = hr as HTMLElement;
      hrEl.classList.add("my-8", "border-0");
      hrEl.style.borderTop = "1px solid rgba(10, 55, 64, 0.2)";
    });

    // Style strong and em
    const strongElements = container.querySelectorAll("strong, b");
    strongElements.forEach((strong) => {
      const strongEl = strong as HTMLElement;
      strongEl.style.fontWeight = "700";
      strongEl.style.color = "#0A3740";
    });

    const emElements = container.querySelectorAll("em, i");
    emElements.forEach((em) => {
      const emEl = em as HTMLElement;
      emEl.style.fontStyle = "italic";
    });
  }, [content]);

  return (
    <>
      {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
      {/* Content is from WordPress CMS (trusted source), not user input */}
      <div
        ref={containerRef}
        className={`prose prose-lg max-w-none ${className}`}
        style={{
          color: "#22333B", // PNN text dark
          fontFamily: "var(--font-outfit)",
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </>
  );
}

